import { mobileLabelString } from '@fs/zion-phone-number'
import { formatBirthDate, parseBirthDate } from '../utils/birthDateUtils'
import {
  churchRecordNumberFieldString,
  emailFieldString,
  firstNamesFieldString,
  lastNamesFieldString,
  usernameFieldString,
} from '../commonStrings'
import { fieldNames } from '../constants'
import { formatFullName } from '../formats'

const keys = {
  [fieldNames.birthDate]: 'cis.profile.birth_date',
  [fieldNames.location]: 'cis.profile.country',
  [fieldNames.email]: 'cis.profile.email',
  [fieldNames.firstNames]: 'cis.profile.given_name',
  [fieldNames.lastNames]: 'cis.profile.surname',
  [fieldNames.churchRecordNumber]: 'cis.profile.member_number',
  [fieldNames.smsCountry]: 'cis.profile.sms.country',
  [fieldNames.smsNumber]: 'cis.profile.sms.number',
  [fieldNames.userId]: 'cis.profile.userid',
  [fieldNames.username]: 'cis.profile.username',
}

export default function getAccountView(profile, t) {
  const alwaysPrivateLabel = t('data-shared-always-private.label', 'Always Private')
  // Show as shared/public if EITHER name field is public, so users are aware of any exposure
  const fullNameShared = profile[keys.firstNames]?.shared || profile[keys.lastNames]?.shared

  const accountViewObj = createAccountViewObj.bind(this, profile)

  return {
    alwaysPrivateLabel,
    fullName: {
      name: 'fullName',
      value: formatFullName({
        givenNames: profile[keys.firstNames]?.value,
        surnames: profile[keys.lastNames]?.value,
      }),
      shared: fullNameShared,
      label: t('full-name.label', 'Full Name'),
    },
    firstNames: accountViewObj(fieldNames.firstNames, {
      label: firstNamesFieldString(),
    }),
    lastNames: accountViewObj(fieldNames.lastNames, {
      label: lastNamesFieldString(),
    }),
    username: accountViewObj(fieldNames.username, {
      label: usernameFieldString(),
    }),
    email: accountViewObj(fieldNames.email, {
      label: emailFieldString(),
    }),
    smsNumber: accountViewObj(fieldNames.smsNumber, {
      label: mobileLabelString(),
    }),
    smsCountry: accountViewObj(fieldNames.smsCountry),
    birthDate: accountViewObj(fieldNames.birthDate, {
      label: t('birth-date.label', 'Birthdate'),
      parse: parseBirthDate,
      format: formatBirthDate,
    }),
    location: accountViewObj(fieldNames.location),
    churchRecordNumber: accountViewObj(fieldNames.churchRecordNumber, {
      label: churchRecordNumberFieldString(),
    }),
  }
}

function getAsAccountItem(profileKey, parse) {
  return function asAccountItem(value, shared) {
    // value ?? this.value logic ensure this works well with the new zion-form in which values are undefined for disabled fields
    return { name: profileKey, value: parse(value ?? this.value), shared: shared ?? this.shared, exists: this.exists }
  }
}

function identity(value) {
  return value
}

function createAccountViewObj(profile, name, { label, parse = identity, format = identity, ...rest } = {}) {
  const profileKey = keys[name]
  const value = profile[profileKey]?.value ?? ''
  return {
    name,
    key: profileKey,
    // Track whether this item exists in CIS or not because we need to make sure
    // not to send empty strings for non-exists data otherwise we will see 465 errors, IDENT-8992
    exists: value !== '',
    value: format(value),
    shared: profile[profileKey]?.shared ?? false,
    label,
    asAccountItem: getAsAccountItem(profileKey, parse),
    ...rest,
  }
}
